import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../../../../app/api/api';
import Menu from '../../../../app/data/models/menu';
import {
  MenuDetails,
  MenuFormData,
  MenuFormModel,
  toMenuFormModel,
} from '../types';
import { UpdateAvailabilityScheduleDto } from '../../../../app/data/models/common/availability';

type Status = 'loading' | 'succeeded' | 'failed' | 'idle';

export interface MenuState {
  menus: Menu[];
  menuListStatus: Status;
  menuDetailsStatus: Status;
  menuFormDataStatus: Status;
  menuCreateStatus: Status;
  menuUpdateStatus: Status;
  menuDeleteStatus: Status;
}

const initialState: MenuState = {
  menus: [],
  menuListStatus: 'idle',
  menuDetailsStatus: 'idle',
  menuFormDataStatus: 'idle',
  menuCreateStatus: 'idle',
  menuUpdateStatus: 'idle',
  menuDeleteStatus: 'idle',
};

export const fetchMenuListAsync = createAsyncThunk<Menu[], string>(
  'menus/fetchList',
  async (restaurantId, thunkApi) => {
    try {
      return await api.Menu.list(`${restaurantId}`);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const createMenuAsync = createAsyncThunk<
  void,
  { restaurantId: string; data: MenuFormData }
>('menus/create', async ({ restaurantId, data }, thunkApi) => {
  try {
    await api.Menu.create(restaurantId, toMenuFormModel(data));
    await thunkApi.dispatch(fetchMenuListAsync(restaurantId));
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const updateMenuAsync = createAsyncThunk<
  void,
  { restaurantId: string; menuId: string; data: MenuFormData }
>('menus/update', async ({ restaurantId, menuId, data }, thunkApi) => {
  try {
    await api.Menu.update(restaurantId, menuId, toMenuFormModel(data));
    await thunkApi.dispatch(fetchMenuListAsync(restaurantId));
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const deleteMenuAsync = createAsyncThunk<
  void,
  { restaurantId: string; menuId: string }
>('menus/delete', async ({ restaurantId, menuId }, thunkApi) => {
  try {
    await api.Menu.delete(restaurantId, menuId);
    await thunkApi.dispatch(fetchMenuListAsync(restaurantId));
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const fetchMenuDetailsAsync = createAsyncThunk<
  MenuDetails,
  { restaurantId: string; menuId: string }
>('menu/details', async ({ restaurantId, menuId }, thunkApi) => {
  try {
    return await api.Menu.details(restaurantId, menuId);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const fetchMenuFormDataAsync = createAsyncThunk<
  MenuFormModel,
  { restaurantId: string; menuId: string }
>('menu/formData', async ({ restaurantId, menuId }, thunkApi) => {
  try {
    return await api.Menu.formData(restaurantId, menuId);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const updateMenuMealItemsAsync = createAsyncThunk<
  void,
  { restaurantId: string; menuId: string; orderedMealItemIds: string[] }
>(
  'menu/updateMealItems',
  async ({ restaurantId, menuId, orderedMealItemIds }, thunkApi) => {
    try {
      await api.Menu.updateMenuMealItems(
        restaurantId,
        menuId,
        orderedMealItemIds
      );
      await thunkApi.dispatch(fetchMenuDetailsAsync({ restaurantId, menuId }));
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateMenuAvailabilityAsync = createAsyncThunk<
  void,
  { restaurantId: string; menuId: string; data: UpdateAvailabilityScheduleDto }
>(
  'menu/updateAvailability',
  async ({ restaurantId, menuId, data }, thunkApi) => {
    try {
      await api.Menu.updateAvailabilities(restaurantId, menuId, data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateMenuOrderAsync = createAsyncThunk<
  void,
  { restaurantId: string; orderedMenuIds: string[] }
>(
  'menus/updateMenuOrder',
  async ({ restaurantId, orderedMenuIds }, thunkAPI) => {
    try {
      await api.Menu.updateOrder(restaurantId, orderedMenuIds);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const menuSlice = createSlice({
  name: 'menus',
  initialState,
  reducers: {
    setMenus: (state, action) => {
      state.menus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(fetchMenuListAsync.pending, (state) => {
        state.menuListStatus = 'loading';
      })
      .addCase(fetchMenuListAsync.fulfilled, (state, action) => {
        state.menus = action.payload;
        state.menuListStatus = 'succeeded';
      })
      .addCase(fetchMenuListAsync.rejected, (state) => {
        state.menuListStatus = 'failed';
      })
      // Details
      .addCase(fetchMenuDetailsAsync.pending, (state) => {
        state.menuDetailsStatus = 'loading';
      })
      .addCase(fetchMenuDetailsAsync.fulfilled, (state) => {
        state.menuDetailsStatus = 'succeeded';
      })
      .addCase(fetchMenuDetailsAsync.rejected, (state) => {
        state.menuDetailsStatus = 'failed';
      })
      // Form Data
      .addCase(fetchMenuFormDataAsync.pending, (state) => {
        state.menuFormDataStatus = 'loading';
      })
      .addCase(fetchMenuFormDataAsync.fulfilled, (state) => {
        state.menuFormDataStatus = 'succeeded';
      })
      .addCase(fetchMenuFormDataAsync.rejected, (state) => {
        state.menuFormDataStatus = 'failed';
      })
      // Create
      .addCase(createMenuAsync.pending, (state) => {
        state.menuCreateStatus = 'loading';
      })
      .addCase(createMenuAsync.fulfilled, (state) => {
        state.menuCreateStatus = 'succeeded';
      })
      .addCase(createMenuAsync.rejected, (state) => {
        state.menuCreateStatus = 'failed';
      })
      // Update
      .addCase(updateMenuAsync.pending, (state) => {
        state.menuUpdateStatus = 'loading';
      })
      .addCase(updateMenuAsync.fulfilled, (state) => {
        state.menuUpdateStatus = 'succeeded';
      })
      .addCase(updateMenuAsync.rejected, (state) => {
        state.menuUpdateStatus = 'failed';
      })
      // Delete
      .addCase(deleteMenuAsync.pending, (state) => {
        state.menuDeleteStatus = 'loading';
      })
      .addCase(deleteMenuAsync.fulfilled, (state) => {
        state.menuDeleteStatus = 'succeeded';
      })
      .addCase(deleteMenuAsync.rejected, (state) => {
        state.menuDeleteStatus = 'failed';
      })
      // Update Menu Meal Items
      .addCase(updateMenuMealItemsAsync.pending, (state) => {
        state.menuUpdateStatus = 'loading';
      })
      .addCase(updateMenuMealItemsAsync.fulfilled, (state) => {
        state.menuUpdateStatus = 'succeeded';
      })
      .addCase(updateMenuMealItemsAsync.rejected, (state) => {
        state.menuUpdateStatus = 'failed';
      });
  },
});

export const { setMenus } = menuSlice.actions;
